import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  HardDrive,
  Zap,
  Eye,
  Shield,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Breadcrumb, message, Button } from "antd";
import Header from "../header/header2";
import axios from "../../utils/axios";

// ---------------------------------------------------------------------------
// Constants (lifted out of render so they are never re-created)
// ---------------------------------------------------------------------------

/** Available FPS options for the user to pick from */
const FPS_OPTIONS = [5, 10, 15, 20, 24, 30];

/**
 * Base bitrate (Mbps) at 30 fps with H.265 for each resolution tier.
 * Storage for any other FPS is derived by scaling linearly:
 *   bitrate_at_N_fps = BASE_BITRATE * (N / 30)
 */
const BASE_BITRATE_AT_30FPS = {
  "2MP HD": 1.5,
  "2K HD": 2.5,
  "4MP": 3.5,
  "4K Ultra HD": 6.0,
  "4K UHD": 6.0,
};

const DEFAULT_BASE_BITRATE = 1.5; // fallback when resolution key is unknown

/** Company filter chips displayed in the toolbar */
const COMPANY_FILTERS = ["All", "Hikvision", "Dahua"];

// ---------------------------------------------------------------------------
// Pure helper – storage calculation
// ---------------------------------------------------------------------------

/**
 * Returns { perDay, perMonth, perYear } in GB for a given resolution + fps.
 *
 * Formula (H.265 assumed):
 *   effectiveBitrate (Mbps) = baseBitrate * (fps / 30)
 *   bytesPerSecond          = effectiveBitrate * 1_000_000 / 8
 *   dailyGB                 = bytesPerSecond * 86_400 / 1_073_741_824
 */
function calculateStorage(resolution, fps) {
  const baseBitrate =
    BASE_BITRATE_AT_30FPS[resolution] ?? DEFAULT_BASE_BITRATE;
  const effectiveBitrate = baseBitrate * (fps / 30);

  const dailyGB = (effectiveBitrate * 1_000_000 * 86_400) / 8 / 1_073_741_824;

  return {
    perDay: dailyGB.toFixed(2),
    perMonth: (dailyGB * 30).toFixed(1),
    perYear: (dailyGB * 365).toFixed(0),
  };
}

// ---------------------------------------------------------------------------
// Small, reusable sub-components
// ---------------------------------------------------------------------------

/**
 * Renders up to `limit` tags, with an expand/collapse toggle when there are
 * more items than the limit.
 */
function ExpandableTags({ items, limit = 3, tagClass, expandClass }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, limit);
  const hasMore = items.length > limit;

  return (
    <div className="flex flex-wrap gap-1.5 items-center">
      {visible.map((item, idx) => (
        <span key={idx} className={tagClass}>
          {item}
        </span>
      ))}
      {hasMore && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((prev) => !prev);
          }}
          className={expandClass}
        >
          {expanded ? (
            <>
              Less <ChevronUp className="w-3 h-3" />
            </>
          ) : (
            <>
              +{items.length - limit} more <ChevronDown className="w-3 h-3" />
            </>
          )}
        </button>
      )}
    </div>
  );
}

/**
 * Maps a camera object to a static image path.
 * Kept as a pure function outside the component.
 */
function getCameraImage(camera) {
  const name = (camera?.name ?? "").toLowerCase();
  const company = (camera?.company ?? "").toLowerCase();

  if (name.includes("hikvision") || company.includes("hikvision"))
    return "/2mphikvision.jpg";
  if (name.includes("dahua") || company.includes("dahua"))
    return "/2mpDahua.jpg";
  return "/camera-default.jpg";
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const SelectCamera = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // ── state ──────────────────────────────────────────────────────────────
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState("All");
  const [selectedFps, setSelectedFps] = useState(30); // default FPS
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const citriot = JSON.parse(localStorage.getItem("citriot")) || {};
  
  // ── derived from location / localStorage ──────────────────────────────
  const { projectName } = location.state || {};
  const projectname = projectName || localStorage.getItem("projectname") || "";
  const user = useMemo(
    () => JSON.parse(localStorage.getItem("user")),
    [] // runs once; user object doesn't change mid-session
  );
  const organizationId = localStorage.getItem("organizationId");

  // ── fetch cameras (runs once on mount) ─────────────────────────────────
  // BUG FIX: removed [mainCategory, selectedDetections] — they were unused
  //          inside the effect and selectedDetections (array) caused infinite
  //          re-renders when not wrapped in useMemo at the call site.
  useEffect(() => {
    const fetchCameras = async () => {
      try {
        const res = await axios.post("/fetch-choosecamera");
        if (res.data.success) {
          setCameras(res.data.data);
        } else {
          message.error(res.data.message || "Failed to fetch cameras");
        }
      } catch {
        message.error("Failed to fetch cameras");
      } finally {
        setLoading(false);
      }
    };

    fetchCameras();
  }, []); // ← intentionally empty; fetch is not parameterised

  // ── filtered list (memoised) ───────────────────────────────────────────
  const filteredCameras = useMemo(() => {
    if (selectedCompany === "All") return cameras;
    return cameras.filter((c) => c.company === selectedCompany);
  }, [cameras, selectedCompany]);

  // ── handlers ───────────────────────────────────────────────────────────

  /**
   * BUG FIX: original code called setSelectedCamera AND navigate in the same
   * tick, so the green "Selected" state was never visible.  Now we await the
   * API call first, then navigate — giving React one frame to paint the
   * selected state (the green button is visible while the POST is in-flight).
   */
  const handleCameraSelect = useCallback(
    async (camera) => {
      setSelectedCamera(camera.id);

      try {
        await axios.post("/update-selectcamera", {
          username: user?.username,
          organizationId,
          projectName: projectname,
          selectedCamera: {
            id: camera.id,
            company: camera.company,
          },
        });
      } catch {
        message.error("Failed to save camera selection");
        setSelectedCamera(null);
        return; // don't navigate on failure
      }

      navigate("/add-camera", { state: { selectedCamera: camera } });
    },
    [user, projectname, navigate]
  );

  // ── render ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <Header auth={citriot?.auth}/>
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-slate-500 text-lg">Loading cameras…</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header auth={citriot?.auth}/>

      {/* Single root wrapper — fixes the double max-w / double-padding bug */}
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 relative overflow-hidden">
        {/* Decorative blobs — BUG FIX: moved to this level so `absolute`
            positioning resolves against the full viewport, not a nested box */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-100/40 to-cyan-100/30 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-violet-100/30 to-purple-100/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-gradient-to-br from-indigo-100/20 to-blue-100/20 rounded-full blur-3xl" />
        </div>

        {/* Content — single constrained column */}
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Breadcrumb */}
          {/* Breadcrumb + Edit Use Cases */}
<div className="flex items-center justify-between mb-5">
  <Breadcrumb
    items={[
      {
        title: (
          <button
            onClick={() => navigate("/create-project")}
            className="text-blue-600 hover:text-blue-700 transition-colors text-sm sm:text-base font-medium bg-none border-none cursor-pointer p-0"
          >
            Home
          </button>
        ),
      },
      {
        title: (
          <span className="text-gray-600 text-sm sm:text-base font-medium">
            Select Camera
          </span>
        ),
      },
    ]}
  />

  <Button
    size="small"
    onClick={() => navigate("/home")}
    className="font-semibold text-blue-600 hover:text-blue-700"
  >
    Edit Use Cases
  </Button>
</div>

          {/* Page header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold mb-3">
              <Shield className="w-3.5 h-3.5" />
              AI-Powered Camera Selection
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1.5">
              Choose Your Perfect Camera
            </h1>
            <p className="text-sm text-slate-500 max-w-xl mx-auto">
              Select from our recommended cameras based on your detection
              requirements
            </p>
          </div>

          {/* ── Toolbar: Company filter + FPS selector ─────────────────── */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8 flex-wrap">
            {/* Company filter */}
            <div className="flex items-center gap-2">
              <span className="text-slate-600 text-sm font-semibold">
                Company:
              </span>
              <div className="flex gap-1.5">
                {COMPANY_FILTERS.map((company) => (
                  <button
                    key={company}
                    onClick={() => setSelectedCompany(company)}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                      selectedCompany === company
                        ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md shadow-blue-200"
                        : "bg-white text-slate-600 border border-slate-200 hover:border-blue-300"
                    }`}
                  >
                    {company}
                  </button>
                ))}
              </div>
            </div>

            {/* Divider (visible on sm+) */}
            <div className="hidden sm:block w-px h-6 bg-slate-200" />

            {/* FPS selector */}
            <div className="flex items-center gap-2">
              <span className="text-slate-600 text-sm font-semibold">
                FPS:
              </span>
              <div className="flex gap-1.5">
                {FPS_OPTIONS.map((fps) => (
                  <button
                    key={fps}
                    onClick={() => setSelectedFps(fps)}
                    className={`w-9 h-8 rounded-lg text-xs font-bold transition-all duration-200 ${
                      selectedFps === fps
                        ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md shadow-blue-200"
                        : "bg-white text-slate-600 border border-slate-200 hover:border-blue-300"
                    }`}
                  >
                    {fps}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── Camera grid ────────────────────────────────────────────── */}
          {filteredCameras.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-full mb-3">
                <Eye className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">
                No Cameras Found
              </h3>
              <p className="text-slate-500 text-sm">
                Try selecting a different company filter
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredCameras.map((camera) => {
                // Storage is recalculated every time selectedFps changes
                const storage = calculateStorage(
                  camera.resolution,
                  selectedFps
                );
                const isHovered = hoveredCard === camera.id;
                const isSelected = selectedCamera === camera.id;

                return (
                  <CameraCard
                    key={camera.id}
                    camera={camera}
                    storage={storage}
                    selectedFps={selectedFps}
                    isHovered={isHovered}
                    isSelected={isSelected}
                    onMouseEnter={() => setHoveredCard(camera.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onSelect={() => handleCameraSelect(camera)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// CameraCard – extracted to its own component so that expanding features on
// one card does NOT re-render every other card.
// ---------------------------------------------------------------------------

function CameraCard({
  camera,
  storage,
  selectedFps,
  isHovered,
  isSelected,
  onMouseEnter,
  onMouseLeave,
  onSelect,
}) {
  return (
    <div
      className={`group relative bg-white rounded-xl border transition-all duration-300 overflow-hidden ${
        isSelected
          ? "border-blue-500 shadow-lg shadow-blue-100 scale-[1.01]"
          : isHovered
          ? "border-blue-300 shadow-lg shadow-blue-50"
          : "border-slate-200 shadow-sm hover:shadow-md"
      }`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Company badge */}
      <div className="absolute top-2.5 right-2.5 z-10">
        <span
          className={`px-2.5 py-0.5 rounded-full text-xs font-bold shadow ${
            camera.company === "Hikvision"
              ? "bg-gradient-to-r from-red-500 to-red-600 text-white"
              : "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
          }`}
        >
          {camera.company}
        </span>
      </div>

      {/* Image strip */}
      <div className="relative h-36 bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden">
        <img
          src={getCameraImage(camera)}
          alt={camera.name}
          className={`w-full h-full object-cover transition-transform duration-500 ${
            isHovered ? "scale-110" : "scale-100"
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Name */}
        <h3 className="text-base font-bold text-slate-900">{camera.name}</h3>

        {/* Quick-spec row */}
        <div className="grid grid-cols-3 gap-2 p-2.5 bg-slate-50 rounded-lg">
          <SpecCell label="Resolution" primary={camera.megapixels} secondary={camera.resolutionDetail} />
          <SpecCell label="FPS" primary={`${selectedFps}`} secondary="Selected" bordered />
          <SpecCell label="AI" primary={camera.ai_capability} secondary="Edge" />
        </div>

        {/* Storage box */}
        <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-1.5 mb-2">
            <HardDrive className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-bold text-slate-700">
              Storage Requirements
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <StorageCell label="Day" value={`${storage.perDay} GB`} />
            <StorageCell label="Month" value={`${storage.perMonth} GB`} highlight />
            <StorageCell label="Year" value={`${storage.perYear} GB`} />
          </div>
        </div>

        {/* Key Features (expandable, max 3 default) */}
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Zap className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-xs font-bold text-slate-700">
              Key Features
            </span>
          </div>
          <ExpandableTags
            items={camera.features}
            limit={3}
            tagClass="px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-md"
            expandClass="px-2 py-1 text-blue-600 text-xs font-semibold flex items-center gap-0.5 hover:text-blue-700 transition-colors cursor-pointer"
          />
        </div>

        {/* Best For (expandable, max 3 default) */}
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Eye className="w-3.5 h-3.5 text-green-500" />
            <span className="text-xs font-bold text-slate-700">Best For</span>
          </div>
          <ExpandableTags
            items={camera.best_for}
            limit={3}
            tagClass="px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-md border border-green-200"
            expandClass="px-2 py-1 text-green-600 text-xs font-semibold flex items-center gap-0.5 hover:text-green-700 transition-colors cursor-pointer"
          />
        </div>

        {/* Additional specs row */}
        <div className="p-2.5 bg-slate-50 rounded-lg space-y-1.5 text-xs">
          <SpecRow label="IR Range" value={camera.specs.ir_range} />
          <SpecRow label="Storage" value={camera.specs.storage} />
          <SpecRow label="Power" value={camera.specs.powerConsumption} />
        </div>

        {/* Select button */}
        <button
          onClick={onSelect}
          className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
            isSelected
              ? "bg-green-500 text-white shadow shadow-green-200"
              : "bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600 shadow shadow-blue-200 hover:shadow-md"
          }`}
        >
          {isSelected ? (
            <>
              <CheckCircle2 className="w-4 h-4" />
              Selected
            </>
          ) : (
            <>
              Select Camera
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tiny presentational helpers (no state, no re-render cost)
// ---------------------------------------------------------------------------

function SpecCell({ label, primary, secondary, bordered }) {
  return (
    <div className={`text-center ${bordered ? "border-x border-slate-200" : ""}`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-xs font-bold text-slate-900 leading-tight">
        {primary}
      </div>
      {secondary && (
        <div className="text-xs text-slate-400">{secondary}</div>
      )}
    </div>
  );
}

function StorageCell({ label, value, highlight }) {
  return (
    <div
      className={`text-center p-1.5 rounded-md ${
        highlight ? "bg-white border border-blue-300" : "bg-white"
      }`}
    >
      <div className="text-xs text-slate-500">{label}</div>
      <div
        className={`font-bold text-blue-600 ${
          highlight ? "text-sm" : "text-xs"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function SpecRow({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{label}:</span>
      <span className="font-semibold text-slate-800">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------

export default SelectCamera;